import { CBox } from '@/pages/__styled'
import colors from '@/styles/colors'
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Upgrade } from '@mui/icons-material'
import { useLocalStorage } from 'usehooks-ts'
import { useState } from 'react'
import dayjs from 'dayjs'
import PricingModal from './PricingModal'
import { useSubscription } from '@/hooks/common/useSubscription'
import { CognitoUser } from '@/types'

export default function Subscription() {
  const theme = useTheme()
  const isLessThanMedium = useMediaQuery(theme.breakpoints.down('md'))
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  const [ewUser] = useLocalStorage<CognitoUser | null>('ewUser', null)
  const { subscription, loading, tier, isPro, openPortal, portalLoading } = useSubscription(ewUser?.userID)

  const planLabel        = tier.charAt(0).toUpperCase() + tier.slice(1)
  const billingLabel     = subscription?.billing === 'yearly' ? '1 Year' : subscription?.billing === 'monthly' ? 'Monthly' : '—'
  const renewalDate      = subscription?.currentPeriodEnd
    ? dayjs(subscription.currentPeriodEnd).format('DD MMM YYYY')
    : '—'
  const updatedDate      = subscription?.updatedAt
    ? dayjs(subscription.updatedAt).format('DD MMM YYYY')
    : dayjs().format('DD MMM YYYY')

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        background: '#ffffff',
        boxShadow:
          '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(226, 232, 240, 0.8)',
        border: 'none',
        borderRadius: '16px',
        px: '28px',
        py: '24px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow:
            '0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(99, 102, 241, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', mb: 3 }}>
        My Subscription
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isLessThanMedium ? 'column' : 'row',
            gap: '32px',
            alignItems: isLessThanMedium ? 'flexStart' : 'flex-start',
          }}
        >
          {/* Current Plan Badge */}
          <Box
            sx={{
              display: 'flex',
              background: isPro
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
              padding: '24px 28px',
              borderRadius: '14px',
              minWidth: '180px',
              boxShadow: isPro ? '0 8px 24px rgba(99, 102, 241, 0.3)' : '0 4px 12px rgba(100, 116, 139, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
                pointerEvents: 'none',
              },
            }}
          >
            <CBox gap={'6px'} sx={{ position: 'relative', zIndex: 1 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                Current Plan
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#ffffff', letterSpacing: '-0.02em' }}>
                {planLabel}
              </Typography>
            </CBox>
          </Box>

          {/* Billing Period */}
          <CBox gap={'6px'}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              Billing Period
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
              {billingLabel}
            </Typography>
          </CBox>

          {/* Since */}
          <CBox gap={'6px'}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              {isPro ? 'Subscribed' : 'Member Since'}
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
              {updatedDate}
            </Typography>
          </CBox>

          {/* Next Renewal */}
          {isPro && (
            <CBox gap={'6px'}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {subscription?.cancelAtPeriodEnd ? 'Ends' : 'Next Renewal'}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: subscription?.cancelAtPeriodEnd ? '#ef4444' : '#0f172a' }}>
                {renewalDate}
              </Typography>
            </CBox>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', ml: isLessThanMedium ? 0 : 'auto', alignItems: 'flex-start', gap: 2 }}>
            {isPro ? (
              <Button
                variant='outlined'
                onClick={openPortal}
                disabled={portalLoading}
                startIcon={portalLoading ? <CircularProgress size={14} /> : undefined}
                sx={{
                  borderRadius: '24px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, textTransform: 'none',
                  borderColor: colors.lightGrey, color: colors.themePurpleTextColorLight,
                  '&:hover': { borderColor: '#9ca3af' },
                }}
              >
                Manage Billing
              </Button>
            ) : (
              <Button
                variant='contained'
                startIcon={<Upgrade />}
                onClick={() => setPricingModalOpen(true)}
                sx={{
                  borderRadius: '24px', padding: '8px 24px', fontSize: '13px', fontWeight: 600, textTransform: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#ffffff',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5558e3 0%, #7c4de8 100%)', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)' },
                }}
              >
                View Plans
              </Button>
            )}
          </Box>
        </Box>
      )}

      <PricingModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} userId={ewUser?.userID} />
    </Box>
  )
}
